/** Use to wrap {x, y} objects and do non-mutating vector math on them. */
export class vec2 {
    static xy(v) {
        return { x: v.x, y: v.y };
    }
    /**
     * Creates a shallow copy of the input vector.
     */
    static clone(src) {
        return { x: src.x, y: src.y };
    }
    static tuple(v) {
        return [v.x, v.y];
    }
    static addScalar(a, b) {
        return { x: a.x + b, y: a.y + b };
    }
    static add(a, b) {
        return { x: a.x + b.x, y: a.y + b.y };
    }
    static subScalar(a, b) {
        return { x: a.x - b, y: a.y - b };
    }
    /**
     * Subtract b from a.
     */
    static sub(a, b) {
        return { x: a.x - b.x, y: a.y - b.y };
    }
    static negate(v) {
        return { x: -v.x, y: -v.y };
    }
    static normalize(v) {
        const len = vec2.len(v);
        return len === 0 ? undefined : { x: v.x / len, y: v.y / len };
    }
    static scale(v, s) {
        s = typeof s === "number" ? { x: s, y: s } : s;
        return { x: v.x * s.x, y: v.y * s.y };
    }
    static midpoint(a, b) {
        // Note: this was using vec2.add and vec2.scale - I revised to inline
        // midpoint calculation to avoid creating intermediate objects
        return { x: (a.x + b.x) * 0.5, y: (a.y + b.y) * 0.5 };
    }
    static centroid(vectors) {
        return {
            x: vectors.reduce((sum, v) => sum + v.x, 0) / vectors.length,
            y: vectors.reduce((sum, v) => sum + v.y, 0) / vectors.length,
        };
    }
    // @deprecated: use scaleToLength, if len is negative, use vec2.negate(..)
    static scaleTo(v, len) {
        const magnitude = vec2.len(v);
        if (magnitude === 0) {
            // Return undefined rather than a zero vector or arbitrary direction, to avoid undefined behavior.
            // --
            // Question: 👆 is a little surprising - scaling "3" on a 1-dimensional number line, to zero, returns "0" - not "undefined"
            // Not sure why scaling a vector on a 2-dimensional plane would behave differently when scaled to zero.
            // Setting length is not a guarantee to maintain vector directionality (eg, a "negative length" would flip the direction)
            // Rather than return undefined, calling code should be responsible for handling zero vector whenever nessisary
            return undefined;
        }
        return vec2.scale(v, len / magnitude);
    }
    /**
     * Sets the length of input vector by specified length. Positive length values are expected. Negative length values
     * are clamped to zero.
     */
    static scaleToLength(v, length) {
        const vLength = vec2.len(v);
        if (vLength === 0) {
            return {
                x: 0,
                y: 0,
            };
        }
        const scale = Math.max(0, length) / vLength;
        return { x: v.x * scale, y: v.y * scale };
    }
    static distance(a, b) {
        return Math.sqrt(vec2.distanceSq(a, b));
    }
    static distanceSq(a, b) {
        return (a.x - b.x) ** 2 + (a.y - b.y) ** 2;
    }
    /**
     * Returns the length (magnitude) of a vector.
     * Optimized with early exit for zero vectors to avoid unnecessary sqrt calculation.
     */
    static len(v) {
        if (vec2.isZero(v)) {
            return 0;
        }
        return Math.sqrt(v.x ** 2 + v.y ** 2);
    }
    static dot(a, b) {
        return a.x * b.x + a.y * b.y;
    }
    /** Angle in radians. If `symmetric=true`, return in range [-PI, +PI], else in range [0, 2PI] */
    static radians(v, symmetric = false) {
        let radians = Math.atan2(v.y, v.x);
        if (!symmetric) {
            radians += radians < 0 ? Math.PI * 2 : 0;
        }
        return radians;
    }
    static perp(v) {
        return { x: -v.y, y: v.x };
    }
    static radiansBetween(v1, v2) {
        const dotProduct = v1.x * v2.x + v1.y * v2.y;
        const magnitudeV1 = Math.sqrt(v1.x * v1.x + v1.y * v1.y);
        const magnitudeV2 = Math.sqrt(v2.x * v2.x + v2.y * v2.y);
        const magnitudesScalar = magnitudeV1 * magnitudeV2;
        const cosTheta = magnitudesScalar === 0 ? 0 : dotProduct / magnitudesScalar;
        return Math.acos(cosTheta);
    }
    /** Value between representing the angle in full turns. Range is [-0.5, +0.5] if `symmetric=true`, else [0, 1] */
    static turns(v, symmetric = false) {
        return vec2.radians(v, symmetric) / (Math.PI * 2);
    }
    static eq(a, b) {
        return a.x === b.x && a.y === b.y;
    }
    /** Rotate counter-clockwise by radians */
    static rotate(v, radians) {
        if (radians === 0)
            return { ...v };
        const s = Math.sin(radians);
        const c = Math.cos(radians);
        /** Simple 2D rotation matrix [[c -s], [s c]] */
        return {
            x: v.x * c - v.y * s,
            y: v.x * s + v.y * c,
        };
    }
    static rotateAround(v, origin, radians) {
        return vec2.add(vec2.rotate(vec2.sub(v, origin), radians), origin);
    }
    static withOrigin(v, origin, op) {
        const transformed = vec2.chain(v).sub(origin);
        return op(transformed).add(origin).xy();
    }
    static isZero(a) {
        return a.x === 0 && a.y === 0;
    }
    static round(v, p) {
        const precision = Math.pow(10, p);
        return { x: Math.round(v.x * precision) / precision, y: Math.round(v.y * precision) / precision };
    }
    static toVec3(v, z = 0) {
        return { x: v.x, y: v.y, z };
    }
    static abs(v) {
        return { x: Math.abs(v.x), y: Math.abs(v.y) };
    }
    static applyMatrix3(v, e) {
        return {
            x: e[0] * v.x + e[3] * v.y + e[6],
            y: e[1] * v.x + e[4] * v.y + e[7],
        };
    }
    static applyMatrix4AsPoint(v, matrix) {
        const x = v.x, y = v.y, z = 0, w = 1;
        const tx = matrix[0] * x + matrix[4] * y + matrix[8] * z + matrix[12] * w;
        const ty = matrix[1] * x + matrix[5] * y + matrix[9] * z + matrix[13] * w;
        const tw = matrix[3] * x + matrix[7] * y + matrix[11] * z + matrix[15] * w;
        if (tw !== 0 && tw !== 1) {
            return { x: tx / tw, y: ty / tw };
        }
        return { x: tx, y: ty };
    }
    static max(a, b) {
        return {
            x: Math.max(a.x, b.x),
            y: Math.max(a.y, b.y),
        };
    }
    static min(a, b) {
        return {
            x: Math.min(a.x, b.x),
            y: Math.min(a.y, b.y),
        };
    }
    static chain(vec) {
        const v = Array.isArray(vec) ? { x: vec[0], y: vec[1] } : vec;
        return {
            add: (v2) => vec2.chain(vec2.add(v, v2)),
            sub: (v2) => vec2.chain(vec2.sub(v, v2)),
            negate: () => vec2.chain(vec2.negate(v)),
            scale: (s) => vec2.chain(vec2.scale(v, s)),
            scaleTo: (len) => {
                const s = vec2.scaleTo(v, len);
                return s ? vec2.chain(s) : undefined;
            },
            len: () => vec2.len(v),
            midpoint: (v2) => vec2.midpoint(v, v2),
            dot: (v2) => vec2.dot(v, v2),
            distance: (v2) => vec2.distance(v, v2),
            normalize: () => {
                const n = vec2.normalize(v);
                return n ? vec2.chain(n) : undefined;
            },
            radians: (symmetric = false) => vec2.radians(v, symmetric),
            turns: (symmetric = false) => vec2.turns(v, symmetric),
            rotate: (radians) => vec2.chain(vec2.rotate(v, radians)),
            rotateAround: (origin, radians) => vec2.chain(vec2.rotateAround(v, origin, radians)),
            eq: (v2) => vec2.eq(v, v2),
            round: (p) => vec2.chain(vec2.round(v, p)),
            xy: () => vec2.xy(v),
            tuple: () => vec2.tuple(v),
            isZero: () => vec2.isZero(v),
            toVec3: (z = 0) => vec2.toVec3(v, z),
            applyMatrix3: (m) => vec2.chain(vec2.applyMatrix3(v, m)),
            max: (a, b) => vec2.chain(vec2.max(a, b)),
            min: (a, b) => vec2.chain(vec2.min(a, b)),
        };
    }
}
//# sourceMappingURL=vec2.js.map